export const CATEGORIES = [
  { id: "3ds", label: "3DS" },
  { id: "card", label: "Card" },
  { id: "customer", label: "Customer" },
  { id: "device", label: "Device" },
  { id: "location", label: "Location" },
  { id: "velocity", label: "Velocity" },
];

export const COUNTRY_OPTIONS = [
  { value: "GB", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "US", label: "United States" },
];

const HIGH_RISK_COUNTRIES = [
  'Algeria', 'Angola', 'Burkina Faso', 'Cameroon', 'Côte d’Ivoire', 'Democratic Republic of the Congo', 'Haiti', 'Kenya', 'Laos (Lao PDR)', 'Lebanon', 'Mali', 'Monaco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal', 'Venezuela'
];

const MEDIUM_RISK_COUNTRIES = [
  'Brazil', 'Croatia', 'Indonesia', 'Nigeria', 'Philippines', 'South Africa', 'South Sudan', 'Syria', 'Vietnam', 'Yemen'
];

const ENGLISH_SPEAKING_COUNTRIES = [
  'United Kingdom', 'United States', 'Ireland', 'Canada', 'Australia', 'New Zealand', 'South Africa'
];

const BASE_RULES = [
  // 3DS RULES
  {
    id: "3DS-001",
    name: "3DS High Decline Velocity",
    param: { type: "number", key: "declineCount", label: "Declines", value: 3, min: 3, max: 1000 },
    category: "3ds",
    enabled: false,
    description:
      "Apply rule after N declined 3DS attempts from same IP within an hour",
  },

  // LOCATION RULES
  {
    id: "CGM-001",
    name: "Card and Geographic Mismatch",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Block transactions where card issuing country and geographic location mismatch (Excluding Moto Transactions)",
  },
  {
    id: "IPA-001",
    name: "Proxy / VPN / Tor Detected",
    category: "ip",
    param: { type: "none" },
    enabled: false,
    description: "Block if IP is anonymised/proxy/VPN/Tor or data-centre hosted.",
  },
  {
    id: "COR-001",
    name: "Block High Risk [Card Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Block transactions originating from high risk card locations",
    list: HIGH_RISK_COUNTRIES,
  },
  {
    id: "COR-002",
    name: "Block Medium Risk [Card Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Block transactions originating from medium risk card locations",
    list: MEDIUM_RISK_COUNTRIES,
  },
  {
    id: "COR-003",
    name: "Allow English Speaking Countries Only [Card Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Allow transactions originating from English speaking card locations only",
    list: ENGLISH_SPEAKING_COUNTRIES,
  },
  {
    id: "GEO-001",
    name: "Block High Risk [Geographic Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Block transactions originating from high risk Geographic locations",
    list: HIGH_RISK_COUNTRIES,
  },
  {
    id: "GEO-002",
    name: "Block Medium Risk [Geographic Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Block transactions originating from medium risk Geographic locations",
    list: MEDIUM_RISK_COUNTRIES,
  },
  {
    id: "GEO-003",
    name: "Allow English Speaking Countries Only [Geographic Origin]",
    category: "location",
    param: { type: "none" },
    enabled: false,
    description: "Allow transactions originating from English speaking countries only",
    list: ENGLISH_SPEAKING_COUNTRIES,
  },

  // CARD RULES
  {
    id: "CARD-001",
    name: "Prepaid/Virtual BIN",
    category: "card",
    param: { type: "none" },
    enabled: false,
    description: "Block or step-up when BIN indicates prepaid/virtual/gift card (often used in testing).",
  },
  {
    id: "CARD-002",
    name: "Repeated CVV Variations",
    category: "card",
    param: { type: "number", key: "attempts", label: "Attempts/30m", value: 3, min: 1, max: 50 },
    enabled: false,
    description: "Block when multiple CVV guesses occur on the same PAN within 30 minutes.",
  },
  {
    id: "CARD-003",
    name: "Expiry Guessing",
    category: "card",
    param: { type: "number", key: "distinctExpiries", label: "Distinct expiries/15m", value: 3, min: 1, max: 50 },
    enabled: false,
    description: "Block when multiple distinct expiry dates are tried for the same PAN in a short window.",
  },

  // CUSTOMER RULES
  {
    id: "CUS-001",
    name: "Disposable/Role-Based Email",
    category: "customer",
    param: { type: "none" },
    enabled: false,
    description: "Block if email domain is disposable or address is role-based (admin@, info@, etc.).",
  },
  {
    id: "CUS-002",
    name: "Name–Cardholder Mismatch",
    category: "customer",
    param: { type: "none" },
    enabled: false,
    description: "Block when provided customer name differs materially from cardholder name.",
  },
  {
    id: "CUS-003",
    name: "Felloh Blacklist Check",
    category: "customer",
    param: { type: "none" },
    enabled: false,
    description: "Block when IP / Card is found in Felloh's blacklist (blacklisted by other customer).",
  },
  {
    id: "CUS-004",
    name: "Payment Method Churn",
    category: "customer",
    param: { type: "number", key: "methodsPerDay", label: "Methods/day", value: 4, min: 1, max: 50 },
    enabled: false,
    description: "Block when the same customer rotates through ≥ N different payment methods in 24h.",
  },

  // DEVICE RULES
  {
    id: "DEV-001",
    name: "Device to Card Velocity",
    category: "device",
    param: { type: "number", key: "cardsPerHour", label: "Distinct cards/hr", value: 3, min: 1, max: 999 },
    enabled: false,
    description: "Block device when device fingerprints against ≥ N distinct cards per hour (card testing).",
  },
  {
    id: "DEV-002",
    name: "Timezone–Geo Mismatch",
    category: "device",
    param: { type: "number", key: "hours", label: "Offset hours ≥", value: 4, min: 1, max: 24 },
    enabled: false,
    description: "Block/step-up when device timezone differs from IP/BIN implied timezone beyond threshold.",
  },
  {
    id: "DEV-003",
    name: "Automation Signals (Headless)",
    category: "device",
    param: { type: "none" },
    enabled: false,
    description: "Block when headless/automation indicators are present (webdriver flags, patched APIs).",
  },

  // VELOCITY RULES
  {
    id: "VEL-001",
    name: "High Decline Velocity",
    category: "velocity",
    param: { type: "number", key: "perHour", label: "Attempts/hr", value: 5, min: 1, max: 999 },
    priority: 7,
    enabled: false,
    description: "Block IP address when declines (excluding MOTO) exceed threshold per hour",
  },
  {
    id: "VEL-002",
    name: "High Capture Velocity",
    category: "velocity",
    param: { type: "number", key: "perDay", label: "Captures/day", value: 5, min: 1, max: 999 },
    enabled: false,
    description: "Block when one card is used in ≥ N successful transactions in 24 hours.",
  },
  {
    id: "VEL-003",
    name: "Email-to-Card Fan-Out",
    category: "velocity",
    param: { type: "number", key: "cardsPerDay", label: "Cards/day", value: 3, min: 1, max: 999 },
    enabled: false,
    description: "Block when one email is used with ≥ N different cards in 24 hours.",
  },
];

export default BASE_RULES;